import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { superAdminAPI } from '../../services/api';

const PlansList = () => {
  const queryClient = useQueryClient();
  const [showModal, setShowModal] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: '',
    price: { monthly: 0, yearly: 0, currency: 'INR' },
    limits: { users: 5, callsPerMonth: 500, storageMB: 250, campaigns: 5, contacts: 1000 },
    features: {
      callRecording: false,
      analytics: true,
      advancedAnalytics: false,
      api: false,
      webhooks: false,
      customBranding: false,
      prioritySupport: false,
      sso: false,
      auditLogs: false,
      whatsappIntegration: false,
      emailIntegration: true,
      mobileApp: false,
      exportData: true,
      googleSheetIntegration: false
    },
    isPopular: false,
    trialDays: 14
  });
  const [formError, setFormError] = useState('');

  const { data: plansData, isLoading } = useQuery({
    queryKey: ['plans'],
    queryFn: async () => {
      console.log('Fetching plans...');
      const result = await superAdminAPI.getPlans();
      console.log('Plans API result:', result);
      return result;
    }
  });

  // API returns { success: true, data: [plans] }
  // axios wraps it, so plansData.data = { success: true, data: [plans] }
  console.log('plansData:', plansData);
  const plans = plansData?.data?.data || [];
  console.log('Extracted plans:', plans);

  const createMutation = useMutation({
    mutationFn: superAdminAPI.createPlan,
    onSuccess: () => {
      queryClient.invalidateQueries(['plans']);
      closeModal();
    },
    onError: (error) => setFormError(error.response?.data?.message || 'Failed to create plan')
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }) => {
      console.log('Updating plan:', { id, data });
      const result = await superAdminAPI.updatePlan(id, data);
      console.log('Update result:', result);
      return result;
    },
    onSuccess: (data) => {
      console.log('Update success:', data);
      queryClient.invalidateQueries(['plans']);
      closeModal();
    },
    onError: (error) => {
      console.error('Update plan error:', error);
      console.error('Error response:', error.response);
      console.error('Error response data:', error.response?.data);
      const errorMsg = error.response?.data?.message || error.message || 'Failed to update plan';
      setFormError(errorMsg);
    }
  });

  const deleteMutation = useMutation({
    mutationFn: superAdminAPI.deletePlan,
    onSuccess: () => queryClient.invalidateQueries(['plans'])
  });

  const closeModal = () => {
    setShowModal(false);
    setEditingPlan(null);
    setFormError('');
    setFormData({
      name: '', slug: '', description: '',
      price: { monthly: 0, yearly: 0, currency: 'INR' },
      limits: { users: 5, callsPerMonth: 500, storageMB: 250, campaigns: 5, contacts: 1000 },
      features: {
        callRecording: false, analytics: true, advancedAnalytics: false, api: false,
        webhooks: false, customBranding: false, prioritySupport: false, sso: false,
        auditLogs: false, whatsappIntegration: false, emailIntegration: true,
        mobileApp: false, exportData: true, googleSheetIntegration: false
      },
      isPopular: false, trialDays: 14
    });
  };

  const openEditModal = (plan) => {
    setEditingPlan(plan);
    setFormData({
      name: plan.name,
      slug: plan.slug,
      description: plan.description || '',
      price: plan.price || { monthly: 0, yearly: 0, currency: 'INR' },
      limits: plan.limits || { users: 5, callsPerMonth: 500, storageMB: 250, campaigns: 5, contacts: 1000 },
      features: plan.features || {},
      isPopular: plan.isPopular || false,
      trialDays: plan.trialDays || 14
    });
    setShowModal(true);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name.startsWith('price.')) {
      const field = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        price: { ...prev.price, [field]: type === 'number' ? parseFloat(value) || 0 : value }
      }));
    } else if (name.startsWith('limits.')) {
      const field = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        limits: { ...prev.limits, [field]: parseInt(value) || 0 }
      }));
    } else if (name.startsWith('features.')) {
      const field = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        features: { ...prev.features, [field]: checked }
      }));
    } else if (type === 'checkbox') {
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else if (type === 'number') {
      setFormData(prev => ({ ...prev, [name]: parseInt(value) || 0 }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
      if (name === 'name' && !editingPlan) {
        const slug = value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
        setFormData(prev => ({ ...prev, slug }));
      }
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setFormError('');

    if (!formData.name || !formData.slug) {
      setFormError('Please fill in all required fields');
      return;
    }

    if (editingPlan) {
      updateMutation.mutate({ id: editingPlan._id, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const handleDelete = (planId, planName) => {
    if (window.confirm(`Are you sure you want to delete the "${planName}" plan? This action cannot be undone.`)) {
      deleteMutation.mutate(planId);
    }
  };

  const formatFeature = (feature) => {
    return feature.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
  };

  return (
    <div className="plans-page">
      <div className="page-header">
        <div className="header-left">
          <h1>Subscription Plans</h1>
          <p>Manage your subscription plans and pricing</p>
        </div>
        <div className="header-right">
          <button className="btn btn-primary" onClick={() => setShowModal(true)}>
            + Create Plan
          </button>
        </div>
      </div>

      {isLoading ? (
        <div className="loading">Loading plans...</div>
      ) : plans.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">📋</div>
          <h3>No plans created</h3>
          <p>Create your first subscription plan to get started</p>
          <button className="btn btn-primary" onClick={() => setShowModal(true)}>Create Plan</button>
        </div>
      ) : (
        <div className="plans-grid">
          {plans.map((plan) => (
            <div key={plan._id} className={`plan-card ${!plan.isActive ? 'inactive' : ''} ${plan.isPopular ? 'popular' : ''}`}>
              {plan.isPopular && <span className="popular-badge">Popular</span>}
              {!plan.isActive && <span className="inactive-badge">Inactive</span>}

              <div className="plan-header">
                <h3>{plan.name}</h3>
                <p className="plan-description">{plan.description}</p>
              </div>

              <div className="plan-pricing">
                <div className="price monthly">
                  <span className="currency">₹{plan.price?.monthly || 0}</span>
                  <span className="period">/month</span>
                </div>
                <div className="price yearly">
                  <span className="amount">₹{plan.price?.yearly || 0}</span>
                  <span className="period">/year</span>
                  {plan.price?.yearly > 0 && (
                    <span className="save">
                      Save ₹{(plan.price?.monthly || 0) * 12 - (plan.price?.yearly || 0)}
                    </span>
                  )}
                </div>
              </div>

              <div className="plan-limits">
                <h4>Limits</h4>
                <ul>
                  <li><span className="label">Users:</span> <span className="value">{plan.limits?.users === -1 ? 'Unlimited' : (plan.limits?.users || 0)}</span></li>
                  <li><span className="label">Calls/mo:</span> <span className="value">{plan.limits?.callsPerMonth === -1 ? 'Unlimited' : (plan.limits?.callsPerMonth || 0).toLocaleString()}</span></li>
                  <li><span className="label">Storage:</span> <span className="value">{plan.limits?.storageMB === -1 ? 'Unlimited' : `${plan.limits?.storageMB || 0}MB`}</span></li>
                  <li><span className="label">Campaigns:</span> <span className="value">{plan.limits?.campaigns === -1 ? 'Unlimited' : (plan.limits?.campaigns || 0)}</span></li>
                  <li><span className="label">Contacts:</span> <span className="value">{plan.limits?.contacts === -1 ? 'Unlimited' : (plan.limits?.contacts || 0).toLocaleString()}</span></li>
                </ul>
              </div>

              <div className="plan-features">
                <h4>Features</h4>
                <ul>
                  {Object.entries(plan.features || {})
                    .filter(([_, enabled]) => enabled)
                    .slice(0, 5)
                    .map(([feature]) => (
                      <li key={feature}><span className="check">✓</span> {formatFeature(feature)}</li>
                    ))}
                  {Object.entries(plan.features || {}).filter(([_, enabled]) => enabled).length > 5 && (
                    <li className="more">
                      +{Object.entries(plan.features || {}).filter(([_, enabled]) => enabled).length - 5} more
                    </li>
                  )}
                </ul>
              </div>

              <div className="plan-footer">
                <button className="btn btn-outline" onClick={() => openEditModal(plan)}>Edit</button>
                <button className="btn btn-danger" onClick={() => handleDelete(plan._id, plan.name)}>Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingPlan ? 'Edit Plan' : 'Create New Plan'}</h2>
              <button className="modal-close" onClick={closeModal}>×</button>
            </div>

            <form onSubmit={handleSubmit} className="modal-form">
              {formError && <div className="error-message">{formError}</div>}

              <div className="form-row">
                <div className="form-group">
                  <label>Plan Name *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Professional"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Slug *</label>
                  <input
                    type="text"
                    name="slug"
                    value={formData.slug}
                    onChange={handleInputChange}
                    placeholder="professional"
                    required
                    disabled={!!editingPlan}
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Best for growing teams"
                  rows={2}
                />
              </div>

              <div className="form-section">
                <h4>Pricing</h4>
                <div className="form-row">
                  <div className="form-group">
                    <label>Monthly Price ($)</label>
                    <input
                      type="number"
                      name="price.monthly"
                      value={formData.price.monthly}
                      onChange={handleInputChange}
                      min="0"
                      step="0.01"
                    />
                  </div>
                  <div className="form-group">
                    <label>Yearly Price ($)</label>
                    <input
                      type="number"
                      name="price.yearly"
                      value={formData.price.yearly}
                      onChange={handleInputChange}
                      min="0"
                      step="0.01"
                    />
                  </div>
                </div>
              </div>

              <div className="form-section">
                <h4>Limits</h4>
                <div className="form-group">
                  <label className="feature-checkbox">
                    <input
                      type="checkbox"
                      name="unlimitedLimits"
                      checked={formData.limits.users === -1}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setFormData(prev => ({
                            ...prev,
                            limits: { users: -1, callsPerMonth: -1, storageMB: -1, campaigns: -1, contacts: -1 }
                          }));
                        } else {
                          setFormData(prev => ({
                            ...prev,
                            limits: { users: 5, callsPerMonth: 500, storageMB: 250, campaigns: 5, contacts: 1000 }
                          }));
                        }
                      }}
                    />
                    Unlimited Access (No Limits)
                  </label>
                </div>
                {formData.limits.users !== -1 && (
                  <>
                    <div className="form-row">
                      <div className="form-group">
                        <label>Users</label>
                        <input type="number" name="limits.users" value={formData.limits.users} onChange={handleInputChange} min="1" />
                      </div>
                      <div className="form-group">
                        <label>Calls/Month</label>
                        <input type="number" name="limits.callsPerMonth" value={formData.limits.callsPerMonth} onChange={handleInputChange} min="0" />
                      </div>
                    </div>
                    <div className="form-row">
                      <div className="form-group">
                        <label>Storage (MB)</label>
                        <input type="number" name="limits.storageMB" value={formData.limits.storageMB} onChange={handleInputChange} min="0" />
                      </div>
                      <div className="form-group">
                        <label>Campaigns</label>
                        <input type="number" name="limits.campaigns" value={formData.limits.campaigns} onChange={handleInputChange} min="0" />
                      </div>
                    </div>
                    <div className="form-row">
                      <div className="form-group">
                        <label>Contacts</label>
                        <input type="number" name="limits.contacts" value={formData.limits.contacts} onChange={handleInputChange} min="0" />
                      </div>
                      <div className="form-group">
                        <label>Trial Days</label>
                        <input type="number" name="trialDays" value={formData.trialDays} onChange={handleInputChange} min="0" />
                      </div>
                    </div>
                  </>
                )}
                {formData.limits.users === -1 && (
                  <div className="form-row">
                    <div className="form-group">
                      <label>Trial Days</label>
                      <input type="number" name="trialDays" value={formData.trialDays} onChange={handleInputChange} min="0" />
                    </div>
                  </div>
                )}
              </div>

              <div className="form-section">
                <h4>Features</h4>
                <div className="features-grid">
                  {[
                    'callRecording', 'analytics', 'advancedAnalytics', 'api', 'webhooks',
                    'customBranding', 'prioritySupport', 'sso', 'auditLogs',
                    'whatsappIntegration', 'emailIntegration', 'mobileApp', 'exportData',
                    'googleSheetIntegration'
                  ].map(feature => (
                    <label key={feature} className="feature-checkbox">
                      <input
                        type="checkbox"
                        name={`features.${feature}`}
                        checked={!!formData.features[feature]}
                        onChange={handleInputChange}
                      />
                      {formatFeature(feature)}
                    </label>
                  ))}
                </div>
              </div>

              <div className="form-group">
                <label className="feature-checkbox">
                  <input
                    type="checkbox"
                    name="isPopular"
                    checked={formData.isPopular}
                    onChange={handleInputChange}
                  />
                  Mark as Popular Plan
                </label>
              </div>

              <div className="modal-actions">
                <button type="button" className="btn btn-outline" onClick={closeModal}>Cancel</button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={createMutation.isPending || updateMutation.isPending}
                >
                  {createMutation.isPending || updateMutation.isPending
                    ? 'Saving...'
                    : editingPlan ? 'Update Plan' : 'Create Plan'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <style>{`
        .plans-page {
          padding: 0;
        }
        .plans-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
          gap: 1.5rem;
        }
        .plan-card {
          background: white;
          border-radius: 0.75rem;
          box-shadow: 0 1px 3px rgba(0,0,0,0.1);
          overflow: hidden;
          position: relative;
          transition: transform 0.2s, box-shadow 0.2s;
        }
        .plan-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0,0,0,0.15);
        }
        .plan-card.inactive {
          opacity: 0.7;
        }
        .plan-card.popular {
          border: 2px solid #E7000B;
        }
        .popular-badge {
          position: absolute;
          top: 1rem;
          right: 1rem;
          background: #E7000B;
          color: white;
          padding: 0.25rem 0.75rem;
          border-radius: 9999px;
          font-size: 0.75rem;
          font-weight: 500;
        }
        .inactive-badge {
          position: absolute;
          top: 1rem;
          left: 1rem;
          background: #6b7280;
          color: white;
          padding: 0.25rem 0.75rem;
          border-radius: 9999px;
          font-size: 0.75rem;
          font-weight: 500;
        }
        .plan-header {
          padding: 1.5rem;
          border-bottom: 1px solid #e5e7eb;
        }
        .plan-header h3 {
          margin: 0 0 0.25rem;
          font-size: 1.25rem;
          font-weight: 600;
        }
        .plan-description {
          margin: 0;
          color: #6b7280;
          font-size: 0.875rem;
        }
        .plan-pricing {
          padding: 1.5rem;
          border-bottom: 1px solid #e5e7eb;
          text-align: center;
        }
        .plan-pricing .price.monthly {
          font-size: 2rem;
          font-weight: 700;
        }
        .plan-pricing .price.yearly {
          color: #6b7280;
          font-size: 0.875rem;
          margin-top: 0.25rem;
        }
        .plan-pricing .save {
          color: #059669;
          font-size: 0.75rem;
          margin-left: 0.5rem;
        }
        .plan-limits, .plan-features {
          padding: 1rem 1.5rem;
          border-bottom: 1px solid #e5e7eb;
        }
        .plan-limits h4, .plan-features h4 {
          margin: 0 0 0.75rem;
          font-size: 0.75rem;
          font-weight: 600;
          color: #6b7280;
          text-transform: uppercase;
        }
        .plan-limits ul, .plan-features ul {
          list-style: none;
          padding: 0;
          margin: 0;
        }
        .plan-limits li {
          display: flex;
          justify-content: space-between;
          padding: 0.25rem 0;
          font-size: 0.875rem;
        }
        .plan-features li {
          padding: 0.25rem 0;
          font-size: 0.875rem;
          color: #059669;
        }
        .plan-features .check {
          margin-right: 0.5rem;
        }
        .plan-features .more {
          color: #6b7280;
          font-style: italic;
        }
        .plan-footer {
          padding: 1rem 1.5rem;
          display: flex;
          gap: 0.5rem;
        }
        .modal-large {
          max-width: 800px;
        }
        .features-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
          gap: 0.5rem;
        }
        .feature-checkbox {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          cursor: pointer;
          font-size: 0.875rem;
        }
        .feature-checkbox input {
          width: 1rem;
          height: 1rem;
        }
      `}</style>
    </div>
  );
};

export default PlansList;